import React from 'react';
import {
  CALCULATION_ROW_OVERSCAN_PX,
  prefixSums,
  visibleCalculationRowWindow,
  type CalculationRowWindow,
  type OfferTableRowLayout,
} from './calculationRowWindow';

export function estimateDocumentRowPx(
  row: {
    kind: string;
    description?: string | null;
    internal_note?: string | null;
    image?: unknown;
    components?: unknown[] | null;
  },
  options: {
    hideDescriptions: boolean;
    expanded: boolean;
    hasTitleSubtotal?: boolean;
    titleSubtotalCount?: number;
  },
): number {
  if (row.kind === 'image') return 120;
  const subtotalCount = options.titleSubtotalCount ?? (options.hasTitleSubtotal ? 1 : 0);
  if (row.kind === 'title') return 36 + subtotalCount * 32;
  if (row.kind === 'text') {
    const lines = Math.max(1, Math.ceil((row.description?.length ?? 0) / 90));
    return 28 + lines * 16;
  }
  let height = 40;
  if (!options.hideDescriptions) {
    const lines = Math.max(1, Math.ceil((row.description?.length ?? 0) / 70));
    height += 8 + lines * 15;
  }
  const noteLen = row.internal_note?.trim().length ?? 0;
  height += noteLen > 0 ? 8 + Math.max(1, Math.ceil(noteLen / 70)) * 14 : 28;
  if ((row.components?.length ?? 0) > 0) height += 22;
  if (options.expanded) height += 28 + (row.components?.length ?? 0) * 18;
  return height;
}

export type OfferTableRowWindowApi = CalculationRowWindow & {
  measureRow: (id: string, node: HTMLTableRowElement | null) => void;
  layoutRef: React.MutableRefObject<OfferTableRowLayout | null>;
};

/**
 * Höhe der Position samt der Zeilen, die direkt dahinter mitgezeichnet werden (Titel-Zwischensummen,
 * aufgeklappte Stückliste) — bis zur nächsten Einfügezeile oder dem Platzhalter des Fensters.
 */
function readRowGroupHeight(row: HTMLTableRowElement): number {
  let height = row.offsetHeight;
  for (let next = row.nextElementSibling; next instanceof HTMLTableRowElement; next = next.nextElementSibling) {
    if (next.hasAttribute('data-offer-drop-index') || next.hasAttribute('aria-hidden')) break;
    height += next.offsetHeight;
  }
  return height;
}

function readBodyOffsetScaled(scrollEl: HTMLElement, tableEl: HTMLElement): number {
  const tbody = tableEl.querySelector('tbody');
  const top = (tbody ?? tableEl).getBoundingClientRect().top;
  const scrollRect = scrollEl.getBoundingClientRect();
  return top - scrollRect.top + scrollEl.scrollTop;
}

/**
 * Sichtbares Zeilenfenster für die Angebotstabelle im Dokument-Scrollcontainer.
 * Höhen sind unskaliert (`offsetHeight`); Scroll/Offset werden durch `scale` umgerechnet.
 */
export function useOfferTableRowWindow(options: {
  enabled: boolean;
  rowIds: readonly string[];
  estimateHeight: (index: number) => number;
  scrollEl: HTMLElement | null;
  tableEl: HTMLElement | null;
  scale: number;
}): OfferTableRowWindowApi {
  const { enabled, rowIds, estimateHeight, scrollEl, tableEl, scale } = options;
  const count = rowIds.length;
  const [layoutVersion, setLayoutVersion] = React.useState(0);
  // Scroll-Position und Maße des Containers leben in Refs: Der Editor ist groß, jedes Rendern teuer.
  // `viewTick` stößt ein Rendern nur an, wenn sich das sichtbare Fenster dadurch tatsächlich verschiebt.
  const [viewTick, setViewTick] = React.useState(0);
  const scrollTopRef = React.useRef(0);
  const viewportHeightRef = React.useRef(640);
  const headerHeightRef = React.useRef(0);
  const bandHeightRef = React.useRef(12);
  /** Scroll-Position, mit der das zuletzt gerenderte Fenster berechnet wurde. */
  const renderedScrollTopRef = React.useRef(0);
  const renderedWindowRef = React.useRef<CalculationRowWindow>({
    start: 0,
    end: 0,
    paddingTop: 0,
    paddingBottom: 0,
  });
  const rowHeightsRef = React.useRef<number[]>([]);
  const heightsRef = React.useRef(new Map<string, number>());
  const nodesRef = React.useRef(new Map<string, HTMLTableRowElement | null>());
  const observersRef = React.useRef(new Map<string, ResizeObserver>());
  const frameRef = React.useRef(0);
  const layoutRef = React.useRef<OfferTableRowLayout | null>(null);

  const publishHeight = React.useCallback((id: string, next: number) => {
    if (next <= 0) return;
    const previous = heightsRef.current.get(id);
    if (previous != null && Math.abs(previous - next) <= 1) return;
    heightsRef.current.set(id, next);
    setLayoutVersion((version) => version + 1);
  }, []);

  const measureRow = React.useCallback(
    (id: string, node: HTMLTableRowElement | null) => {
      if (!enabled) return;
      const previous = nodesRef.current.get(id) ?? null;
      if (previous === node) return;
      nodesRef.current.set(id, node);
      observersRef.current.get(id)?.disconnect();
      observersRef.current.delete(id);
      if (!node) return;
      const read = () => publishHeight(id, readRowGroupHeight(node));
      read();
      const observer = new ResizeObserver(read);
      observer.observe(node);
      observersRef.current.set(id, observer);
    },
    [enabled, publishHeight],
  );

  React.useEffect(
    () => () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      for (const observer of observersRef.current.values()) observer.disconnect();
    },
    [],
  );

  const safeScale = scale > 0 ? scale : 1;
  const computeWindow = React.useCallback(
    (): CalculationRowWindow =>
      enabled
        ? visibleCalculationRowWindow({
            count,
            heights: rowHeightsRef.current,
            trailing: bandHeightRef.current,
            scrollTop: scrollTopRef.current / safeScale,
            viewportHeight: viewportHeightRef.current / safeScale,
            headerHeight: headerHeightRef.current / safeScale,
            overscanPx: CALCULATION_ROW_OVERSCAN_PX / safeScale,
          })
        : { start: 0, end: count, paddingTop: 0, paddingBottom: 0 },
    [count, enabled, safeScale],
  );
  const computeWindowRef = React.useRef(computeWindow);
  computeWindowRef.current = computeWindow;

  // Beim Scrollen nur `scrollTop` lesen (billig) und erst rendern, wenn der Overscan zur Hälfte
  // aufgebraucht ist und sich das Fenster wirklich verschiebt. Größenänderungen von Container oder
  // Tabelle lesen zusätzlich den Tabellen-Offset neu: Schrumpft das Dokument, weil geschätzte Zeilen
  // kleiner gemessen werden, klemmt der Browser `scrollTop` ohne verlässliches `scroll`-Event — ein
  // alter Wert zeigte dann hinter das Tabellenende und das Fenster blieb leer (nur Platzhalter).
  React.useEffect(() => {
    if (!enabled || !scrollEl) {
      scrollTopRef.current = 0;
      return;
    }
    const readChrome = (): boolean => {
      let changed = false;
      const nextHeight = scrollEl.clientHeight;
      if (nextHeight > 0 && nextHeight !== viewportHeightRef.current) {
        viewportHeightRef.current = nextHeight;
        changed = true;
      }
      if (!tableEl) return changed;
      const nextOffset = readBodyOffsetScaled(scrollEl, tableEl);
      if (nextOffset > 0 && Math.abs(nextOffset - headerHeightRef.current) >= 0.5) {
        headerHeightRef.current = nextOffset;
        changed = true;
      }
      const bandH = tableEl.querySelector<HTMLElement>('[data-offer-drop-index]')?.offsetHeight ?? 0;
      if (bandH > 0 && bandH !== bandHeightRef.current) {
        bandHeightRef.current = bandH;
        changed = true;
      }
      return changed;
    };
    const sync = (force: boolean) => {
      scrollTopRef.current = scrollEl.scrollTop;
      if (
        !force &&
        Math.abs(scrollTopRef.current - renderedScrollTopRef.current) < CALCULATION_ROW_OVERSCAN_PX / 2
      ) {
        return;
      }
      const next = computeWindowRef.current();
      const prev = renderedWindowRef.current;
      if (
        force ||
        next.start !== prev.start ||
        next.end !== prev.end ||
        next.paddingTop !== prev.paddingTop ||
        next.paddingBottom !== prev.paddingBottom
      ) {
        setViewTick((tick) => tick + 1);
      }
    };
    const schedule = (task: () => void) => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = 0;
        task();
      });
    };
    const onScroll = () => schedule(() => sync(false));
    const onResize = () => schedule(() => sync(readChrome()));
    sync(readChrome());
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    const observer = new ResizeObserver(onResize);
    observer.observe(scrollEl);
    if (tableEl) observer.observe(tableEl);
    return () => {
      scrollEl.removeEventListener('scroll', onScroll);
      observer.disconnect();
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    };
  }, [enabled, scrollEl, tableEl, count, layoutVersion, scale]);

  const bandHeight = bandHeightRef.current;
  const heights = React.useMemo(
    () =>
      rowIds.map((id, index) => {
        const measured = heightsRef.current.get(id);
        return bandHeight + (measured ?? estimateHeight(index));
      }),
    // `viewTick` greift Band-/Containermaße aus den Refs auf, die sich ohne neue Zeilenhöhe ändern können.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bandHeight, estimateHeight, layoutVersion, rowIds, viewTick],
  );
  rowHeightsRef.current = heights;

  const rowWindow = computeWindow();
  renderedWindowRef.current = rowWindow;
  renderedScrollTopRef.current = scrollTopRef.current;

  layoutRef.current = enabled
    ? {
        count,
        prefix: prefixSums(heights),
        heights,
        bandHeight,
        bodyOffsetScaled: headerHeightRef.current,
        scale: safeScale,
      }
    : null;

  return { ...rowWindow, measureRow, layoutRef };
}
